import * as React from 'react'
import { useState } from 'react'

// Helper function to make GraphQL requests to Shopify Storefront API
const shopifyFetch = async (query, variables = {}) => {
  const response = await fetch(
    `https://${process.env.GATSBY_SHOPIFY_STORE_URL}/api/2024-01/graphql.json`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }
  )

  const result = await response.json()

  if (result.errors) {
    console.error('Shopify GraphQL errors:', result.errors)
    throw new Error(JSON.stringify(result.errors))
  }

  return result.data
}

const defaultValues = {
  cart: [],
  isOpen: false,
  isCartOpen: false,
  loading: false,
  refreshCount: 0,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  toggleCartOpen: () => {},
  updateCurrentPageUrl: () => {},
  checkout: {
    lineItems: [],
  },
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localStorageKey = `shopify_cart_id`

export const StoreProvider = ({ children }) => {
  const [checkout, setCheckout] = useState(defaultValues.checkout)
  const [loading, setLoading] = useState(false)
  const [isCartOpen, setCartOpen] = useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = useState(false)
  const [refreshCount, setRefreshCount] = useState(defaultValues.refreshCount)
  const [currentPageUrl, setCurrentPageUrl] = useState('')

  const updateCurrentPageUrl = (url) => {
    setCurrentPageUrl(url)
  }

  const toggleCartOpen = () => {
    setRefreshCount(refreshCount + 1)
    setCartOpen(!isCartOpen)
  }

  // Transform Cart API response to checkout format for compatibility
  const transformCartToCheckout = (cart) => {
    if (!cart) return defaultValues.checkout

    return {
      id: cart.id,
      webUrl: cart.checkoutUrl || '',
      lineItems: (cart.lines?.edges || []).map(({ node }) => ({
        id: node.id,
        title: node.merchandise?.product?.title || '',
        variant: {
          id: node.merchandise?.id || '',
          title: node.merchandise?.title || 'Default Title',
          image: node.merchandise?.image || {},
          priceV2: node.merchandise?.priceV2 || node.merchandise?.price || { amount: '0', currencyCode: 'USD' },
          product: node.merchandise?.product || {},
        },
        quantity: node.quantity || 0,
      })),
      subtotalPriceV2: cart.cost?.subtotalAmount || { amount: '0', currencyCode: 'USD' },
      totalPriceV2: cart.cost?.totalAmount || { amount: '0', currencyCode: 'USD' },
    }
  }

  const setCheckoutItem = (cart) => {
    if (isBrowser && cart?.id) {
      localStorage.setItem(localStorageKey, cart.id)
    }
    setCheckout(transformCartToCheckout(cart))
  }

  React.useEffect(() => {
    const initializeCart = async () => {
      const existingCartID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null

      if (existingCartID && existingCartID !== `null`) {
        try {
          const query = `
            query getCart($cartId: ID!) {
              cart(id: $cartId) {
                id
                checkoutUrl
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          priceV2: price {
                            amount
                            currencyCode
                          }
                          image {
                            src: url
                            altText
                          }
                          product {
                            title
                          }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
              }
            }
          `
          const data = await shopifyFetch(query, { cartId: existingCartID })
          if (data.cart) {
            setCheckoutItem(data.cart)
            return
          }
        } catch (e) {
          console.error('Error fetching cart:', e)
          localStorage.setItem(localStorageKey, null)
        }
      }

      // Create new cart
      try {
        const mutation = `
          mutation {
            cartCreate {
              cart {
                id
                checkoutUrl
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          priceV2: price {
                            amount
                            currencyCode
                          }
                          image {
                            src: url
                            altText
                          }
                          product {
                            title
                          }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        `
        const data = await shopifyFetch(mutation)
        setCheckoutItem(data.cartCreate.cart)
      } catch (e) {
        console.error('Error creating cart:', e)
      }
    }

    initializeCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addVariantToCart = async (variantId, quantity) => {
    setLoading(true)

    try {
      const mutation = `
        mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        priceV2: price {
                          amount
                          currencyCode
                        }
                        image {
                          src: url
                          altText
                        }
                        product {
                          title
                        }
                      }
                    }
                  }
                }
              }
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      `

      const data = await shopifyFetch(mutation, {
        cartId: checkout.id,
        lines: [
          {
            merchandiseId: variantId,
            quantity: parseInt(quantity, 10),
          },
        ],
      })

      setCheckoutItem(data.cartLinesAdd.cart)
      setLoading(false)
      setDidJustAddToCart(true)
      setTimeout(() => setDidJustAddToCart(false), 3000)
    } catch (e) {
      console.error('Error adding to cart:', e)
      setLoading(false)
    }
  }

  const removeLineItem = async (cartId, lineItemID) => {
    setLoading(true)

    try {
      const mutation = `
        mutation removeFromCart($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              checkoutUrl
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        priceV2: price {
                          amount
                          currencyCode
                        }
                        image {
                          src: url
                          altText
                        }
                        product {
                          title
                        }
                      }
                    }
                  }
                }
              }
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      `

      const data = await shopifyFetch(mutation, {
        cartId,
        lineIds: [lineItemID],
      })

      setCheckoutItem(data.cartLinesRemove.cart)
      setLoading(false)
    } catch (e) {
      console.error('Error removing from cart:', e)
      setLoading(false)
    }
  }

  const updateLineItem = async (cartId, lineItemID, quantity) => {
    setLoading(true)

    try {
      const mutation = `
        mutation updateCart($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        priceV2: price {
                          amount
                          currencyCode
                        }
                        image {
                          src: url
                          altText
                        }
                        product {
                          title
                        }
                      }
                    }
                  }
                }
              }
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      `

      const data = await shopifyFetch(mutation, {
        cartId,
        lines: [
          {
            id: lineItemID,
            quantity: parseInt(quantity, 10),
          },
        ],
      })

      setCheckoutItem(data.cartLinesUpdate.cart)
      setLoading(false)
    } catch (e) {
      console.error('Error updating cart:', e)
      setLoading(false)
    }
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        toggleCartOpen,
        updateCurrentPageUrl,
        currentPageUrl,
        checkout,
        loading,
        didJustAddToCart,
        isCartOpen,
        refreshCount,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}
